import { useCallback, useEffect, useRef } from 'react';
import type { Trend } from '../previous';

/**
 * Press-and-hold timings. Deliberately unhurried: a runaway stepper that blows
 * past the weight you wanted is worse than one extra second of holding.
 *
 * A tap fires once. Holding waits HOLD_DELAY_MS before repeating at all, so a
 * normal tap never triggers it, then repeats steadily and eases up slightly
 * once it is clear the hold is intentional.
 */
const HOLD_DELAY_MS = 450;
const REPEAT_MS = 190;
const REPEAT_FAST_MS = 110;
/** Repeats at the steady rate before easing to the faster one. */
const TICKS_BEFORE_FAST = 6;

interface Props {
  label: string;
  value: number | null;
  onStep: (delta: number) => void;
  step: number;
  disabled?: boolean;
  /** Colours the number against last session: green up, red down. */
  trend?: Trend;
  /**
   * Enables press-and-hold repeat. Only worth it where the range is wide —
   * weight. Rep counts are small enough that a hold would overshoot.
   */
  hold?: boolean;
}

/**
 * Minus/plus number control: minus on the left, plus on the right, both
 * full-height touch targets. Number entry never opens a keyboard or shows
 * spinner arrows.
 *
 * Holding a button repeats the step, so a big jump in weight is one long press
 * rather than twenty taps.
 *
 * An unset value reads as 0 rather than a dash — a blank slot is ambiguous
 * mid-set, and 0 is the honest starting point for a stepper.
 */
export default function Stepper({ label, value, onStep, step, disabled, trend, hold }: Props) {
  const shown = value ?? 0;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ticks = useRef(0);
  // Set on pointerdown so the click that follows it is not counted twice.
  const handledByPointer = useRef(false);

  const stop = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    ticks.current = 0;
  }, []);

  // Never leave a repeat running behind an unmounted or disabled control.
  useEffect(() => stop, [stop]);
  useEffect(() => { if (disabled) stop(); }, [disabled, stop]);

  const startHold = useCallback((delta: number) => {
    stop();
    const tick = () => {
      onStep(delta);
      ticks.current += 1;
      timer.current = setTimeout(tick, ticks.current >= TICKS_BEFORE_FAST ? REPEAT_FAST_MS : REPEAT_MS);
    };
    timer.current = setTimeout(tick, HOLD_DELAY_MS);
  }, [onStep, stop]);

  function onPointerDown(delta: number, isDisabled: boolean) {
    if (isDisabled) return;
    handledByPointer.current = true;
    onStep(delta);                  // the tap itself
    if (hold) startHold(delta);     // and the hold, where enabled
  }

  function onClick(delta: number, isDisabled: boolean) {
    // Pointer already handled it; this is the synthetic click that follows.
    if (handledByPointer.current) {
      handledByPointer.current = false;
      return;
    }
    // Keyboard activation (Enter/Space) never fires pointerdown.
    if (!isDisabled) onStep(delta);
  }

  const minusDisabled = !!disabled || shown <= 0;

  return (
    <div className="stepper" role="group" aria-label={label}>
      <button
        type="button"
        className="stepper-btn"
        disabled={minusDisabled}
        aria-label={`Decrease ${label} by ${step}`}
        onPointerDown={() => onPointerDown(-step, minusDisabled)}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        onClick={() => onClick(-step, minusDisabled)}
        onContextMenu={e => e.preventDefault()}
      >
        −
      </button>
      <span className={`stepper-value${trend ? ` stepper-value--${trend}` : ''}`}>{shown}</span>
      <button
        type="button"
        className="stepper-btn"
        disabled={disabled}
        aria-label={`Increase ${label} by ${step}`}
        onPointerDown={() => onPointerDown(step, !!disabled)}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        onClick={() => onClick(step, !!disabled)}
        onContextMenu={e => e.preventDefault()}
      >
        +
      </button>
    </div>
  );
}
